





























import { Component, Prop } from 'vue-property-decorator';
import { default as DxDataSource } from 'devextreme/data/data_source';

import container from '@/container';
import { Page } from '@/core/components/controls';
import { DataSource } from '@/types';
import { Inject } from '@/core/helpers/vue-inversify-decorator';
import {
    CONTRACTS,
    Events,
    FetchServiceContract,
    EventsServiceContract,
} from '@/core/contracts';
import { ContextsState } from '@/store';
import { Table, TableOptions, getTableCaption } from '@/core/components/table';
import { StructuredResult, PlannedOperation } from '@/dto';
import { PlannedOperationsTable } from '@/pages/plannedOperations';
import { ResourcesUsagePageArgs, ResourcesUsageInfo } from './types';
import ResourcesUsageChart from './ResourcesUsageChart.vue';
import { lookupField } from '@/core/helpers/utils';
import config from '@/config';
import { debounce } from 'lodash';

@Component({
    components: {
        TableOptions,
        ResourcesUsageChart,
        PlannedOperationsTable,
    },
})
export default class ResourcesUsagePage extends Page {
    @Prop({ default: () => ({}) }) public args!: ResourcesUsagePageArgs;

    // ChartData is based on dataSource, that can be changed by Table events.
    // ChartData updating after dataSource reloading during the dataSource.onChanged().
    // In this way chart reactively rebuilt after interaction with table.
    public chartData: any[] = [];
    public table: any = null;
    public isChartVisible: boolean = false;
    public tableKey: string = 'resources-usage-control';
    public chartOnPageRatio: number = 30; // % of Page component body height

    @Inject(CONTRACTS.FetchService) private fetchService?: FetchServiceContract;
    @Inject(CONTRACTS.EventsService)
    private _eventsService?: EventsServiceContract;
    private resizeDebounced = debounce(
        this.onResize.bind(this),
        config.events.chartResizeDelay
    );

    public setTable(table: Table): void {
        this.table = table;
    }

    public resized() {
        this.resizeDebounced();
    }

    private onResize() {
        (this.$refs.chart as ResourcesUsageChart).refresh();
    }

    public get dataSource(): DataSource {
        return new DxDataSource({
            load: this.load,
            onChanged: this.updateChartData,
            filter: this.filter,
            // This field is necessary to avoid chart redrawing during
            // scrolling of table with big count of records
            pageSize: Infinity,
        });
    }

    public get tableName(): string {
        return getTableCaption(this.tableKey);
    }

    public get chartWrapperStyleObject(): object {
        return this.args.onlyChart
            ? { height: '100%' }
            : this.isChartVisible
            ? { height: `${this.chartOnPageRatio}%` }
            : { height: '0', visibility: 'hidden' };
    }

    public get tableWrapperStyleObject(): object {
        return {
            height: `${
                100 - (this.isChartVisible ? this.chartOnPageRatio : 0)
            }%`,
            paddingTop: '16px',
            boxSizing: 'border-box',
        };
    }

    private updateChartData(): void {
        if (this.args.onlyChart) {
            let { type } = this.args;

            if (type !== undefined) {
                // Specifically convert the single string type into array
                // to be able use type.includes() for checking in code below.
                if (typeof type === 'string') {
                    type = [type];
                }
                this.args.rawData = this.args.rawData!.filter(v => {
                    if (Boolean(v.type) && type!.includes(v.type!)) {
                        return v;
                    }
                });
            }
        }

        this.chartData = this.createChartDataFromTableData(
            this.args.onlyChart ? this.args.rawData! : this.dataSource.items()
        );
    }

    private createChartDataFromTableData(
        tableData: PlannedOperation[]
    ): ResourcesUsageInfo[] {
        const usageDates = new Set<string>();

        tableData.forEach(v => {
            const startDate = new Date(v.startTime!);
            const finishDate = new Date(v.finishTime!);

            while (startDate <= finishDate) {
                const localeDateString = startDate.toDateString();
                usageDates.add(localeDateString);
                startDate.setDate(startDate.getDate() + 1);
            }
        });

        const objectsUsageInfo: { date: string; objects: string[] }[] = [];

        usageDates.forEach(dt => {
            const findDate = new Date(dt);
            const result = tableData.filter(v => {
                const startDate = new Date(
                    new Date(v.startTime!).toDateString()
                );
                const finishDate = new Date(
                    new Date(v.finishTime!).toDateString()
                );
                return startDate <= findDate && finishDate >= findDate;
            });
            const usageResources: string[] = [];

            result.forEach(v => {
                if (usageResources.indexOf(v.mainResourceId) === -1) {
                    usageResources.push(v.mainResourceId);
                }
            });

            if (usageResources.length > 0) {
                objectsUsageInfo.push({
                    date: findDate.toDateString(),
                    objects: usageResources,
                });
            }
        });

        return objectsUsageInfo.map(objectUsageInfo => ({
            date: new Date(objectUsageInfo.date),
            count: objectUsageInfo.objects.length,
        }));
    }

    private created() {
        if (this.args.onlyChart) {
            this.updateChartData();
        } else {
            this._eventsService!.on(
                Events.ProcessingFinished,
                this.onProcessingFinished
            );
        }
    }

    private beforeDestroy() {
        this._eventsService!.off(
            Events.ProcessingFinished,
            this.onProcessingFinished
        );
    }

    private onProcessingFinished({ contextId }: any) {
        if (Number(ContextsState.currentContextId) === Number(contextId)) {
            this.dataSource.reload();
        }
    }

    private filter(operation: PlannedOperation): boolean {
        let { type } = this.args;

        if (type === undefined) {
            return false;
        }

        // Specifically convert the single string type into array
        // to be able use type.includes() for checking in code below.
        if (typeof type === 'string') {
            type = [type];
        }
        return Boolean(operation.type) && type!.includes(operation.type!);
    }

    private async load(): Promise<PlannedOperation[]> {
        const currentContextId = ContextsState.currentContextId;

        const url = `/contexts/${currentContextId}/operations?`;
        return await container
            .get<FetchServiceContract>(CONTRACTS.FetchService)
            .get<StructuredResult<PlannedOperation>>(url)
            .then(this.transformData);
    }

    private transformData(
        data: StructuredResult<PlannedOperation>
    ): PlannedOperation[] {
        if (
            (data === undefined || data.items === undefined || data === null) &&
            !this.args.rawData
        ) {
            return [];
        }

        if (this.args.rawData !== undefined) {
            return this.args.rawData;
        }

        return data.items.map(item => {
            lookupField(item, 'mainResource', data.vehicles);
            lookupField(item, 'startSite', data.sites);
            lookupField(item, 'finishSite', data.sites);
            lookupField(item, 'type', data.strings);
            lookupField(item, 'linkedResource', data.vehicles);
            item.mainResourceType =
                data.resourceTypes[item.mainResource.typeId];
            if (item.linkedResource) {
                item.linkedResourceType =
                    data.resourceTypes[item.linkedResource.typeId];
            }
            return item;
        });
    }
}
